/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/common/app-config.service';
import { ProxyDataService } from '@app/core/http';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { LayoutService } from '@app/ui/common/layout.service';
import { MessageService } from '@core/lib/services/message.service';
import { DataTransformationService } from '@app/common/data-transformation.service';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { CountUpdate } from '@app/data-model/count-update';
import { DataModelService } from '@app/data-model/data-model.service';
import { WorkItemService } from '@app/data-model/item/work-item.service';
import { deleteUndefinedNullValues } from '@core/lib/helpers/helpers';
import { UtilService } from '@app/util/util.service';

@Injectable({
  providedIn: 'root'
})
export class DataAccessService {
  private toDoData;
  private priorities = [];

  constructor(
    private appConfigService: AppConfigService,
    private dataService: ProxyDataService,
    private userPrivilegesService: UserPrivilegesService,
    private userPreferenceService: UserPreferenceService,
    private layoutService: LayoutService,
    private messageService: MessageService,
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private dataModelService: DataModelService,
    private dataTransformationUtil: DataTransformationService,
    private workItemService: WorkItemService
  ) {}

  calculateToDoTotals(data) {
    const counts = {} as any;
    counts.interruptedUpdate = this.dataModelService.getCountUpdate('unsigned', 0, 0, 0, 0, 0, 0, {});
    counts.assignedUpdate = this.dataModelService.getCountUpdate('assigned', 0, 0, 0, 0, 0, 0, {});
    counts.attendingReadUpdate = this.dataModelService.getCountUpdate('attendingread', 0, 0, 0, 0, 0, 0, {});
    counts.toDoUpdate = this.dataModelService.getCountUpdate('to-do', 0, 0, 0, 0, 0, 0, {});
    counts.nawCoreportUpdate = this.dataModelService.getCountUpdate('nawCoreport', 0, 0, 0, 0, 0, 0, {});
    counts.nawReviewRequestedUpdate = this.dataModelService.getCountUpdate('nawReviewRequested', 0, 0, 0, 0, 0, 0, {});

    data.attendingReadItems = data.attendingReadItems === null ? [] : data.attendingReadItems;
    _.reduce(
      data.savedItems,
      (update, value) => {
        return this.toDoItemCountReducer(update, value);
      },
      counts.interruptedUpdate
    );
    _.reduce(
      data.assignedItems,
      (update, value) => {
        return this.toDoItemCountReducer(update, value);
      },
      counts.assignedUpdate
    );
    _.reduce(
      data.attendingReadItems,
      (update, value) => {
        return this.toDoItemCountReducer(update, value);
      },
      counts.attendingReadUpdate
    );
    _.reduce(
      data.nawCoreportItems,
      (update, value) => {
        return this.toDoItemCountReducer(update, value);
      },
      counts.nawCoreportUpdate
    );
    _.reduce(
      data.nawReviewRequestedItems,
      (update, value) => {
        return this.toDoItemCountReducer(update, value);
      },
      counts.nawReviewRequestedUpdate
    );

    const totals = _.chain(data.assignedItems)
      .union(data.savedItems)
      .union(data.attendingReadItems)
      .union(data.nawCoreportItems)
      .union(data.nawReviewRequestedItems)
      .uniq(this.uniqueTodoList)
      .groupBy('procedurePriorityId')
      .value();

    let statCount = 0;
    let routineCount = 0;
    const countsObj = {
      stat: 0,
      routine: 0
    };

    this.priorities = this.searchFilterCriteriaService.getExamPriorities();
    _.each(totals, (value, key) => {
      if (Number(key) > 0 && this.getCounterFlag(Number(key), this.priorities)) {
        statCount = statCount + value.length;
      } else {
        routineCount = routineCount + value.length;
      }
    });
    countsObj.stat = statCount;
    countsObj.routine = routineCount;

    _.reduce(countsObj, this.toDoAggregateReducer, counts.toDoUpdate);
    counts.toDoUpdate.stat = countsObj.stat || 0;
    counts.toDoUpdate.findings = totals.findings || 0;
    return counts;
  }
  /**
   * get the number of days from today date
   * @param timeOfExam
   */
  getNumberOfDays(timeOfExam) {
    const currentDate = new Date();
    return this.dataTransformationUtil.extractNumberOfDays(timeOfExam, currentDate);
  }

  /**
   * Gets the count for one or more database lists, optionally with a filter. If multiple IDs are given with a
   * controller, the same filter is applied to each worklist while getting the count.
   *
   * @param ids {string|number|Array} The ID or IDs of the worklist(s) being counted.
   * @param [filter] {object|Array} The filter object or array of filter objects to use.
   * @returns {Promise<CountUpdate>} A promise that is resolved with CountUpdate objects.
   */
  getDatabaseListCount(ids, filter?) {
    const params = {} as any;
    if (ids instanceof Array) {
      ids = ids.join();
    }

    params.id = ids;

    if (filter) {
      params.filter = UtilService.parseFilter(filter);
    }

    return new Promise((resolve, reject) => {
      this.dataService.getByIdParams('worklists', 'item-count', params).then(
        (data: Array<any>) => {
          const updates = [];
          const isViewConfidentialPatient = this.userPrivilegesService.hasPrivilege('ViewConfidentialPatient');
          const isInstituteSeparationEnabled = this.appConfigService.isInstituteSeparationEnabled();
          const accessibleInstitutes = Array.isArray(this.userPrivilegesService.institutes)
            ? this.userPrivilegesService.institutes
            : [];
          for (let i = 0; i < data.length; ++i) {
            //if the totalItemsCnt is less than zero, then the count for this list is disabled.
            if (data[i].available) {
              updates.push(
                new CountUpdate(data[i], isViewConfidentialPatient, accessibleInstitutes, isInstituteSeparationEnabled)
              );
            }
          }
          resolve(updates);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  /**
   * Gets the count for to-do lists whenever the filter is applied.
   *
   * @param workListObj worklist for which filter is applied on
   * @returns {Promise<CountUpdate>} A promise that is resolved with CountUpdate objects.
   */
  getTodoListCount(workListObj) {
    let examStatus;
    if (workListObj.id === 'unsigned') {
      const unsignedFilter = this.userPreferenceService.getUserPreferenceFromCache('unsigned-filter');
      if (unsignedFilter === 'transcribed') {
        examStatus = 70;
      } else if (unsignedFilter === 'dictated') {
        examStatus = 60;
      }
    } else if (workListObj.id === 'attendingread') {
      examStatus = 70;
    }
    let params = {
      worklistId: workListObj.worklistId,
      worklistName: workListObj.id,
      actorName: this.appConfigService.getUserId()
    } as any;
    // append isAttendingRead if attending review is enabled
    if (
      this.userPrivilegesService.hasPrivilege('ID_AttendingReviewWL') &&
      this.appConfigService.getAppConfigByKey('attendingReview') === 'true'
    ) {
      params = _.extend(params, { isAttendingRead: false });
    }

    if (examStatus) {
      params.examStatus = examStatus;
    }
    if (workListObj.filterData) {
      params.filter = UtilService.parseFilter(workListObj.filterData);
    }

    return new Promise((resolve, reject) => {
      this.dataService.get('worklists/filtered-todo-item-count', params).then(
        (data: any) => {
          let assignedUpdate: CountUpdate, attendingReadUpdate: CountUpdate, interruptedUpdate: CountUpdate;

          if (data.assignedItems != null) {
            assignedUpdate = this.dataModelService.getCountUpdate('assigned', 0, 0, 0, 0, 0, 0, {});
            _.reduce(
              data.assignedItems,
              (update, value) => {
                return this.toDoItemCountReducer(update, value);
              },
              assignedUpdate
            );
          } else if (data.attendingReadItems != null) {
            attendingReadUpdate = this.dataModelService.getCountUpdate('attendingread', 0, 0, 0, 0, 0, 0, {});
            _.reduce(
              data.attendingReadItems,
              (update, value) => {
                return this.toDoItemCountReducer(update, value);
              },
              attendingReadUpdate
            );
          } else if (data.savedItems != null) {
            interruptedUpdate = this.dataModelService.getCountUpdate('unsigned', 0, 0, 0, 0, 0, 0, {});
            _.reduce(
              data.savedItems,
              (update, value) => {
                return this.toDoItemCountReducer(update, value);
              },
              interruptedUpdate
            );
          }

          resolve([interruptedUpdate, assignedUpdate, attendingReadUpdate]);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  getLocalToDoData() {
    return this.toDoData;
  }

  setLocalToDoData(data) {
    this.toDoData = data;
  }

  /**
   * Gets the count for to-do lists, optionally with a filter. If multiple IDs are given with a
   * controller, the same filter is applied to each worklist while getting the count.
   *
   * @param workItemStateWorklistId {string} the ID of the workitemstate worklist
   * @param reportStateWorklistId {string} the ID of the reportstate worklist
   * @param attendingReadWorklistId {string} the ID of the attendingRead worklist
   * @param nawCoreportWorklistId {string} the ID of the nawCoreport worklist
   * @param nawReviewRequestedWorklistId {string} the ID of the nawReviewRequested worklist
   * @returns {Promise<CountUpdate>} A promise that is resolved with CountUpdate objects.
   */
  getTodoWorklistsCount(
    workItemStateWorklistId,
    reportStateWorklistId,
    attendingReadWorklistId,
    nawCoreportWorklistId,
    nawReviewRequestedWorklistId
  ) {
    const unsignedFilter = this.userPreferenceService.getUserPreferenceFromCache('unsigned-filter');
    let unsignedFilterCode;
    const attendingReadFilterCode = 70;
    if (unsignedFilter === 'transcribed') {
      unsignedFilterCode = 70;
    } else if (unsignedFilter === 'dictated') {
      unsignedFilterCode = 60;
    }

    let params = {
      examStateWorklistId: workItemStateWorklistId,
      reportStateWorklistId: reportStateWorklistId,
      attendingReadWorklistId: attendingReadWorklistId,
      actorName: this.appConfigService.getUserId(),
      attendingReadExamStatus: attendingReadFilterCode,
      nawCoreportWorklistId: nawCoreportWorklistId,
      nawReviewRequestedWorklistId: nawReviewRequestedWorklistId
    } as any;
    // append isAttendingRead if attending review is enabled
    if (
      this.userPrivilegesService.hasPrivilege('ID_AttendingReviewWL') &&
      this.appConfigService.getAppConfigByKey('attendingReview') === 'true'
    ) {
      params = _.extend(params, { isAttendingRead: false });
    }
    //append examStatus param for the count rest endpoint based on unsigned user preference.
    if (unsignedFilterCode) {
      params = _.extend(params, { examStatus: unsignedFilterCode });
    }

    deleteUndefinedNullValues(params);

    return new Promise((resolve, reject) => {
      this.dataService.get('worklists/todo/item-count', params).then(
        data => {
          this.toDoData = data;
          const counts = this.calculateToDoTotals(this.toDoData);
          resolve([
            counts.interruptedUpdate,
            counts.assignedUpdate,
            counts.toDoUpdate,
            counts.attendingReadUpdate,
            counts.nawCoreportUpdate,
            counts.nawReviewRequestedUpdate
          ]);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  /**
   * Gets workItems for a given worklist endpoint with the given parameters returning a promise
   * that resolves with a Page and another promise.
   *
   * @param endpoint
   * @param id
   * @param [parameters] {object} optional parameters to be used
   * @param [parameters.filter] {object|Array} The filter object or array of filter objects to use.
   * @param [parameters.sort] {object} The sort object to use.
   * @param [parameters.pageStart] {number} The first entry in a page.
   * @param [parameters.pageEnd] {number} The last entry in a page.
   * @param [parameters.pageNumber] {number} The page number of a page.
   * @returns {object} A promise that resolves with an object containing a page property with the created Page
   *   and another promise that resolves with a list of StateUpdates.
   */
  getWorkItems(endpoint, id, parameters) {
    const params = this.getWorkItemParams(parameters);
    let items;
    if (!_.isEmpty(params)) {
      items = this.dataService.getByIdParams(endpoint, id, params);
    } else {
      items = this.dataService.getById(endpoint, id);
    }

    return new Promise((resolve, reject) => {
      items
        .then(data => {
          if (data[0] && data[0].length) {
            const items = data[0] as [];
            const workItems = this.workItemService.createWorkItems(id, items, parameters);
            if (workItems && workItems.length) {
              resolve({ items: workItems, count: data[1] });
            } else {
              resolve({ count: data[1] });
            }
          } else {
            resolve({ count: data[1] });
          }
        })
        .catch(error => {
          //error handling exists elsewhere in the application, just reject the promise
          reject(error);
        });
    });
  }

  private toDoItemCountReducer(update, value) {
    update.total++;
    this.priorities = this.searchFilterCriteriaService.getExamPriorities();
    update.stat +=
      value.procedurePriorityId > 0 && this.getCounterFlag(value.procedurePriorityId, this.priorities) ? 1 : 0;
    update.findings += value.aiFinding > 25 ? 1 : 0;
    return update;
  }

  private uniqueTodoList(item) {
    return item.procedureId;
  }

  private getCounterFlag(id, priorities) {
    return _.pluck(
      _.filter(priorities, priority => {
        return priority.priorityId === id;
      }),
      'counterValue'
    )[0];
  }

  private toDoAggregateReducer(update, priority) {
    update.total = (update.total || 0) + priority;
    return update;
  }

  private extractedGetWorkItemParams(parameters: any, params: any) {
    if (parameters.filter) {
      params.filter = UtilService.parseFilter(parameters.filter);
    }

    if (!!parameters.sort && !_.isEmpty(parameters.sort.item)) {
      params.sort = UtilService.joinParams(parameters.sort.item);
    }

    if (parameters.examStateWorklistId) {
      params.examStateWorklistId = parameters.examStateWorklistId;
    }

    if (parameters.reportStateWorklistId) {
      params.reportStateWorklistId = parameters.reportStateWorklistId;
    }

    if (parameters.examStatus) {
      params.examStatus = parameters.examStatus;
    }

    if (parameters.attendingReadWorklistId) {
      params.attendingReadWorklistId = parameters.attendingReadWorklistId;
    }

    if (parameters.attendingReadExamStatus) {
      params.attendingReadExamStatus = parameters.attendingReadExamStatus;
    }

    if (parameters.isAttendingRead !== undefined) {
      params.isAttendingRead = parameters.isAttendingRead;
    }

    if (parameters.currentContextPatientId !== undefined) {
      params.currentContextPatientId = parameters.currentContextPatientId;
    }

    if (parameters.currentPatientGroupingIndex !== undefined) {
      params.currentPatientGroupingIndex = parameters.currentPatientGroupingIndex;
    }

    //see above
    if (parameters.pageStart > 0 && parameters.pageEnd > parameters.pageStart) {
      params.startR = parameters.pageStart;
      params.endR = parameters.pageEnd;
    }
  }

  // Used for Querying worklist only for needed attributes
  private getWorkItemParams(parameters) {
    const params = {} as any;
    if (parameters) {
      params.attributes = this.layoutService.getActiveWLColumnsAttrId();
      this.extractedGetWorkItemParams(parameters, params);
    }

    return params;
  }
}
